import { apiBaseUrl } from './api-url.js';
import { currentAccessToken } from './auth.js';
import { ledger } from './browser.js';

/**
 * REDEEMING AN INVITATION — the browser half of `POST /invites/redeem`.
 *
 * ⚠️ NOT A tRPC CALL, AND IT CANNOT BE. Every procedure in the router resolves an identity and
 * refuses a subject with no row in `users` — which is exactly the caller here. The route stands
 * apart for that reason (`apps/api/src/http/invite-route.ts` carries the full argument), so
 * this is a plain authenticated POST, the same shape as `export.ts` and `import.ts`.
 *
 * ⚠️ THE BODY CARRIES THE TOKEN AND NOTHING ELSE. The email address is NOT sent: the server
 * takes it from the verified JWT's `email` claim and matches it against the invitation there.
 * Sending an address from here would be handing the server the one fact it must not take from
 * a client — a forwarded link plus any account would be enough to spend somebody else's
 * invitation (§5 check 3). If a future edit adds a field to this body, that is the check it is
 * quietly undoing.
 */

/** Long enough for a cold API container; short enough that a person is not left staring. */
const REDEEM_TIMEOUT_MS = 20_000;

export type JoinResult =
  | { ok: true; message: string }
  /** `retryable` decides whether the screen offers the button again or sends them for a new
   *  invitation. Classified from the STATUS, never from the sentence. */
  | { ok: false; message: string; retryable: boolean };

export async function redeemInvitation(token: string): Promise<JoinResult> {
  const base = apiBaseUrl();
  if (!base) return { ok: false, message: 'This build has no API configured.', retryable: false };

  const bearer = await currentAccessToken();
  if (!bearer) {
    return { ok: false, message: 'Sign in first, then open your invitation link again.', retryable: true };
  }

  try {
    const response = await fetch(`${base}/invites/redeem`, {
      method: 'POST',
      headers: { authorization: `Bearer ${bearer}`, 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
      signal: AbortSignal.timeout(REDEEM_TIMEOUT_MS),
    });
    const body = (await response.json().catch(() => ({}))) as { ok?: boolean; reason?: string; message?: string };

    if (response.ok && body.ok === true) {
      return { ok: true, message: body.message ?? 'You are in.' };
    }

    /**
     * ⚠️ THE SERVER'S SENTENCE IS SHOWN AS-IS AND NOT REPHRASED HERE. It already flattens six
     * different refusals into one on purpose — telling somebody which condition failed is free
     * reconnaissance for anyone testing tokens — and a friendlier client-side rewrite would
     * either leak that distinction back or contradict the API.
     */
    const message = body.reason ?? 'That did not work.';
    // 429 and 5xx are worth pressing again; 401/403/409 are not, and offering a retry on a
    // refusal is how somebody ends up pressing a button twenty times at a dead invitation.
    const retryable = response.status === 429 || response.status >= 500;
    if (response.status >= 500) {
      // The refusals are normal outcomes and earn no ledger row — the same deny-list reasoning
      // the route itself uses. An infrastructure failure is not normal and does.
      ledger.record(new Error(`redeem failed: ${String(response.status)}`), 'invites.redeem', { notified: true });
    }
    return { ok: false, message, retryable };
  } catch (error) {
    ledger.record(error, 'invites.redeem', { notified: true });
    return { ok: false, message: 'Could not reach the server. Try again in a moment.', retryable: true };
  }
}
